import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const destinations = [
  "Rajasthan", "Kerala", "Golden Triangle", "Himalayan Retreats", "Goa",
  "Maldives", "Dubai", "Switzerland", "Japan", "Europe",
  "Bhutan", "Sri Lanka", "African Safari", "Other / Bespoke",
];

const budgets = [
  "$5,000 – $10,000",
  "$10,000 – $25,000",
  "$25,000 – $50,000",
  "$50,000 – $100,000",
  "$100,000+",
];

const travelers = ["Solo", "Couple", "Family (3–4)", "Family (5–7)", "Private Group (8+)"];

export default function QuoteForm({ compact = false, defaultDestination = "" }) {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    destination: defaultDestination, travelers: "", budget_range: "",
    travel_dates: "", preferences: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you. Your concierge will be in touch within 24 hours.");
      setForm({ full_name: "", email: "", phone: "", destination: defaultDestination, travelers: "", budget_range: "", travel_dates: "", preferences: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-transparent border-b border-border focus:border-accent transition-colors py-3 outline-none text-sm placeholder:text-foreground/30";
  const labelCls = "eyebrow block mb-1";

  return (
    <form onSubmit={submit} className={`w-full ${compact ? "" : "max-w-3xl"}`} data-testid="quote-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <div>
          <label className={labelCls}>Full Name</label>
          <input data-testid="quote-name" required value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder="Eleanor Whitfield" />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input data-testid="quote-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
        </div>
        <div>
          <label className={labelCls}>Phone (optional)</label>
          <input data-testid="quote-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91 98 …" />
        </div>
        <div>
          <label className={labelCls}>Destination</label>
          <select data-testid="quote-destination" required value={form.destination} onChange={set("destination")} className={`${inputCls} appearance-none cursor-pointer`}>
            <option value="">Select a destination</option>
            {destinations.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Travellers</label>
          <select data-testid="quote-travelers" required value={form.travelers} onChange={set("travelers")} className={`${inputCls} appearance-none cursor-pointer`}>
            <option value="">Select group size</option>
            {travelers.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Budget Range (USD)</label>
          <select data-testid="quote-budget" required value={form.budget_range} onChange={set("budget_range")} className={`${inputCls} appearance-none cursor-pointer`}>
            <option value="">Select budget</option>
            {budgets.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Approximate Travel Dates</label>
          <input data-testid="quote-dates" value={form.travel_dates} onChange={set("travel_dates")} className={inputCls} placeholder="e.g. March 12 – 22, 2026" />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Travel Preferences</label>
          <textarea data-testid="quote-preferences" rows={4} value={form.preferences} onChange={set("preferences")} className={`${inputCls} resize-none`} placeholder="Honeymoon, dietary preferences, special occasions, dream experiences…" />
        </div>
      </div>

      <div className="mt-10 flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-[0.25em] text-foreground/50">Reply within 24 hours</p>
        <button
          type="submit"
          disabled={loading}
          data-testid="quote-submit"
          className="group inline-flex items-center gap-3 px-8 py-4 bg-foreground text-background hover:bg-accent hover:text-accent-foreground transition-colors duration-300 uppercase text-[11px] tracking-[0.3em] disabled:opacity-50"
        >
          {loading ? "Sending…" : "Request a Quote"}
          <ArrowUpRight size={14} strokeWidth={1.5} className="group-hover:rotate-45 transition-transform duration-500" />
        </button>
      </div>
    </form>
  );
}
